import type { Store } from "@/data/repositories";
import type { Api } from "./ApiClient";
import { applySnapshot, type ArchiveService, type Compressor } from "./ArchiveService";

/**
 * Moves the diary between two browsers on the same phone (Safari and the
 * installed app keep separate storage) or between devices: a snapshot waits
 * on the server for a few minutes behind a six-digit code, readable once.
 */
export class TransferService {
  constructor(
    private readonly api: Api,
    private readonly store: Store,
    private readonly archive: ArchiveService,
    private readonly compressor: Compressor,
  ) {}

  async createCode(): Promise<{ code: string; expiresAt: string }> {
    const snapshot = await this.archive.snapshot();
    const body = await this.compressor.gzip(new TextEncoder().encode(JSON.stringify(snapshot)));
    return this.api.transferCreate(body);
  }

  /** True when this device already holds entries, so the caller can ask before replacing them. */
  async hasEntries(): Promise<boolean> {
    return (await this.store.entries.exportAll()).some((e) => !e.deleted);
  }

  async redeem(code: string): Promise<void> {
    const bytes = await this.api.transferFetch(code.replace(/\D/g, ""));
    await applySnapshot(this.store, await this.compressor.gunzip(bytes));
  }
}
